import React, { useState, useEffect } from "react";
import "../Style/AllReport.css";
import axios from "axios";
import { BASE_URL } from "../Apis/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FileDown, Search, Calendar, Home, FileText, ChevronDown, ChevronUp, X } from "lucide-react";

const AllReport = ({ selectedReport = "" }) => {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [Flats, setFlats] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [memberName, setMemberName] = useState("");
  const [subjectSelected, setSubjectSelected] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);

  const token = localStorage.getItem("token");
  const society = localStorage.getItem("society");

  useEffect(() => {
    const fetchFlats = async () => {
      try {
        const resp = await axios.get(
          `${BASE_URL}/api/member/get-list?society=${encodeURIComponent(society)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFlats(resp.data || []);
      } catch (err) {
        console.error("fetch flats error:", err);
        setError("Failed to fetch flats.");
      }
    };
    if (token && society) fetchFlats();
  }, [token, society]);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const resp = await axios.get(
          `${BASE_URL}/chat/get-title-by-user-chat?societyName=${encodeURIComponent(society)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSubjects(resp.data || []);
      } catch (err) {
        console.error("fetch subjects error:", err);
        setError("Failed to fetch subjects.");
      }
    };
    if (token) fetchSubjects();
  }, [token, society]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fromDate || !toDate || !memberName || !subjectSelected) {
      setError("Please select From Date, To Date, Flat, and Subject.");
      return;
    }
    setError("");

    try {
      const resp = await axios.post(
        `${BASE_URL}/chat/get-filter-data-by-title`,
        null,
        {
          params: {
            subject: subjectSelected,
            societyName: society,
            flatNo: memberName,
            fromDate,
            toDate,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setFilteredData(resp.data || []);
      toast.success("Data fetched successfully!");
    } catch (err) {
      console.error("fetch filtered data error:", err);
      setError("Failed to fetch filtered data.");
      toast.error("Failed to fetch data!");
    }
  };

  const handleCancel = () => {
    setFromDate("");
    setToDate("");
    setMemberName("");
    setSubjectSelected("");
    setFilteredData([]);
    setError("");
    setExpandedRows([]);
  };

  const toggleReadMore = (index) => {
    if (expandedRows.includes(index)) {
      setExpandedRows(expandedRows.filter((i) => i !== index));
    } else {
      setExpandedRows([...expandedRows, index]);
    }
  };

  const downloadPDF = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error("No data to download!");
      return;
    }

    try {
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const doc = new jsPDF("p", "pt", "a4");
      doc.setFontSize(18);
      doc.text("Filtered Report", 40, 50);

      const tableColumn = ["Flat No", "Member Name", "Title", "Message", "Date"];
      const tableRows = filteredData.map((item) => [
        item.unitNo ?? "",
        item.memberName ?? "",
        item.title ?? "",
        (item.message ?? "").replace(/<\/?[^>]+(>|$)/g, "").slice(0, 1000),
        item.date ? new Date(item.date).toLocaleString() : "",
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 80,
        styles: {
          fontSize: 10,
          cellPadding: 6,
          overflow: "linebreak",
        },
        headStyles: { fillColor: [8, 125, 183], textColor: 255 },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 120 },
          2: { cellWidth: 120 },
          3: { cellWidth: 220 },
          4: { cellWidth: 100 },
        },
        margin: { left: 40, right: 40 },
        didDrawPage: (data) => {
          const pageHeight = doc.internal.pageSize.height;
          doc.setFontSize(9);
          doc.text(`Generated: ${new Date().toLocaleString()}`, 40, pageHeight - 30);
        },
      });

      doc.save("Filtered_Report.pdf");
      toast.success("PDF downloaded successfully!");
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.error("Failed to generate PDF. Check console for details.");
    }
  };

  return (
    <div className="all-report-wrapper">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <div className="report-content">
        <div className="filter-section">
          <form className="filter-form" onSubmit={handleSubmit}>
            <div className="filter-grid">
              <div className="filter-field">
                <label className="filter-label">
                  <Calendar size={14} />
                  From Date
                </label>
                <input
                  type="date"
                  className="filter-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="filter-field">
                <label className="filter-label">
                  <Calendar size={14} />
                  To Date
                </label>
                <input
                  type="date"
                  className="filter-input"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              <div className="filter-field">
                <label className="filter-label">
                  <Home size={14} />
                  Flat
                </label>
                <select
                  className="filter-input"
                  value={memberName}
                  onChange={(e) => setMemberName(e.target.value)}
                >
                  <option value="">Select Flat</option>
                  {Flats.map((flat, idx) => (
                    <option key={idx} value={flat.unit_no ?? flat.name}>
                      {flat.unit_no ?? flat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label className="filter-label">
                  <FileText size={14} />
                  Subject
                </label>
                <select
                  className="filter-input"
                  value={subjectSelected}
                  onChange={(e) => setSubjectSelected(e.target.value)}
                >
             
              <option value="">Select Subject</option>
                <option value="ALL">All</option>
              {subjects.map((subj, idx) => (
                <option key={idx} value={subj.title}>
                  {subj.title}
                </option>
              ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="filter-error">
                <span>{error}</span>
              </div>
            )}

            <div className="filter-actions">
              <button type="submit" className="filter-btn filter-btn-primary">
                <Search size={16} />
                Get Report
              </button>
              <button type="button" className="filter-btn filter-btn-secondary" onClick={handleCancel}>
                <X size={16} />
                Clear
              </button>
            </div>
          </form>
        </div>
        {filteredData.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <div className="results-info">
                <h3 className="results-title">Report Results</h3>
                <span className="results-count">{filteredData.length} records found</span>
              </div>
              <button className="download-btn" onClick={downloadPDF}>
                <FileDown size={16} />
                Download PDF
              </button>
            </div>

            <div className="results-table-wrapper">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Flat No</th>
                    <th>Member Name</th>
                    <th>Title</th>
                    <th>Message</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.unitNo}</td>
                      <td>{item.memberName}</td>
                      <td>{item.title}</td>
                      <td>
                        {item.message?.length > 100 ? (
                          <div className="message-cell">
                            <div className="message-text">
                              {expandedRows.includes(idx) ? item.message : item.message.slice(0, 100) + "..."}
                            </div>
                            <button
                              className="expand-btn"
                              onClick={() => toggleReadMore(idx)}
                            >
                              {expandedRows.includes(idx) ? (
                                <>
                                  <ChevronUp size={14} />
                                  Show less
                                </>
                              ) : (
                                <>
                                  <ChevronDown size={14} />
                                  Show more
                                </>
                              )}
                            </button>
                          </div>
                        ) : (
                          item.message
                        )}
                      </td>
                      <td>{item.date ? new Date(item.date).toLocaleString() : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllReport;
